import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Grid,
  Button,
} from "@mui/material";
import {
  ExpandMore,
  LocationOn,
  CalendarToday,
  WbSunny,
  Brightness3,
  LightMode,
  AccessTime,
  Translate,
  ExpandLess,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import AppointmentBookingModal from "./AppointmentBookingModal";

const inferImageMimeFromBase64 = (base64 = "") => {
  const t = (base64 || "").trim();
  if (t.startsWith("/9j")) return "image/jpeg";
  if (t.startsWith("iVBOR")) return "image/png";
  if (t.startsWith("R0lGOD")) return "image/gif";
  return "image/png";
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const countSlots = (shiftData) => {
  if (!Array.isArray(shiftData)) return 0;
  return shiftData.reduce(
    (n, g) => n + (Array.isArray(g.slots) ? g.slots.length : 0),
    0,
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// ── Single shift section (expanded by default, collapsible) ──────────────────
const ShiftSection = ({ title, shiftIcon, shiftData, onSlotSelect }) => {
  const [collapsed, setCollapsed] = useState(false);
  if (!Array.isArray(shiftData) || shiftData.length === 0) return null;
  const totalSlots = countSlots(shiftData);
  if (totalSlots === 0) return null;

  return (
    <div style={{ marginBottom: 4 }}>
      {/* Shift header row */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 0 6px",
          cursor: "pointer",
          userSelect: "none",
          borderTop: "1px solid #f0f4f8",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {shiftIcon}
          <Typography fontWeight={600} fontSize={15} color="#0d1b3d">
            {title}
          </Typography>
          <span
            style={{
              background: "#eef6fb",
              color: "#177f97",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 20,
              padding: "2px 10px",
            }}
          >
            {totalSlots} slots
          </span>
        </div>
        {collapsed ? (
          <ExpandMore style={{ color: "#aaa", fontSize: 20 }} />
        ) : (
          <ExpandLess style={{ color: "#aaa", fontSize: 20 }} />
        )}
      </div>

      {/* Slot groups */}
      {!collapsed &&
        shiftData.map((group, idx) => (
          <div key={idx} style={{ marginBottom: 14 }}>
            {group.time_range && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  margin: "8px 0 8px",
                }}
              >
                <AccessTime style={{ fontSize: 14, color: "#aaa" }} />
                <Typography variant="caption" color="#777" fontWeight={500}>
                  {group.time_range}
                </Typography>
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(group.slots || []).map((slot, sIdx) => {
                const occupied = slot.status === "occupied";
                return (
                  <button
                    key={sIdx}
                    disabled={occupied}
                    onClick={() =>
                      !occupied && onSlotSelect(slot, group.time_range)
                    }
                    style={{
                      minWidth: 108,
                      padding: "8px 12px",
                      border: occupied
                        ? "1px solid #e8e8e8"
                        : "1px solid #d0dce8",
                      borderRadius: 10,
                      background: occupied ? "#f8f8f8" : "#fff",
                      cursor: occupied ? "not-allowed" : "pointer",
                      textAlign: "center",
                      boxShadow: occupied
                        ? "none"
                        : "0 1px 4px rgba(0,0,0,0.06)",
                      transition: "border-color 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!occupied)
                        e.currentTarget.style.borderColor = "#0E3F66";
                    }}
                    onMouseLeave={(e) => {
                      if (!occupied)
                        e.currentTarget.style.borderColor = "#d0dce8";
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: occupied ? "#ccc" : "#0d1b3d",
                      }}
                    >
                      {slot.slot_start_time}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: occupied ? "#ddd" : "#888",
                        marginTop: 2,
                      }}
                    >
                      – {slot.slot_end_time}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const DoctorSlotsDisplay = ({
  doctors,
  job_id,
  patient,
  specialization,
  orgCode,
  appMode,
  authToken,
  onGoToMyAppointments,
}) => {
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  if (!doctors || !Array.isArray(doctors) || doctors.length === 0) return null;

  const handleSlotSelect = (doctor, slot, timeRange, shiftNumber) => {
    if (slot.status === "occupied") {
      toast.warning("This slot is already booked");
      return;
    }
    setSelectedDoctor(doctor);
    setSelectedSlot({ ...slot, timeRange, shiftNumber });
    setBookingModalOpen(true);
  };

  const handleBookingClose = () => {
    setBookingModalOpen(false);
    setSelectedDoctor(null);
    setSelectedSlot(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {doctors.map((doctor, index) => {
        const slots = doctor?.next_available_slots;
        const imageSrc = doctor.photo
          ? `data:${inferImageMimeFromBase64(doctor.photo)};base64,${doctor.photo}`
          : null;

        const displayName =
          `${doctor.salutation ? doctor.salutation + " " : ""}${doctor.name || ""}`.trim();
        const initials = getInitials(doctor.name || displayName);

        const languages = Array.isArray(doctor.languages)
          ? doctor.languages.join(", ")
          : doctor.languages || "";

        const experience =
          doctor.experience || doctor.years_of_experience || doctor.exp || "";

        return (
          <div
            key={doctor.doctor_id || index}
            style={{
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 2px 16px rgba(14,63,102,0.08)",
              border: "1px solid #e8f0fa",
              overflow: "hidden",
            }}
          >
            {/* ── Doctor info ── */}
            <div style={{ padding: "20px 22px 14px" }}>
              <div
                style={{ display: "flex", alignItems: "flex-start", gap: 16 }}
              >
                {/* Avatar */}
                <div style={{ position: "relative", flexShrink: 0 }}>
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt={displayName}
                      style={{
                        width: 70,
                        height: 70,
                        borderRadius: 14,
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 70,
                        height: 70,
                        borderRadius: 14,
                        background: "#0E3F66",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 22,
                        letterSpacing: 1,
                      }}
                    >
                      {initials}
                    </div>
                  )}
                  {/* Online dot */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 2,
                      right: 2,
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: "#17b6a8",
                      border: "2px solid #fff",
                    }}
                  />
                </div>

                {/* Name + meta */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    fontWeight={700}
                    fontSize={18}
                    color="#0d1b3d"
                    noWrap
                  >
                    {displayName}
                  </Typography>
                  <Typography variant="body2" color="#666" sx={{ mt: 0.3 }}>
                    {doctor.specialization || specialization || ""}
                    {experience ? ` · ${experience} yrs exp` : ""}
                  </Typography>

                  {/* Chips row */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginTop: 10,
                    }}
                  >
                    {(doctor.location?.city || doctor.location?.state) && (
                      <span style={chipStyle}>
                        <LocationOn style={{ fontSize: 13, color: "#555" }} />
                        {[doctor.location?.city, doctor.location?.state]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    )}
                    {doctor.consultation_fees !== undefined && (
                      <span style={chipStyle}>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#555",
                          }}
                        >
                          ₹
                        </span>
                        {doctor.consultation_fees}
                      </span>
                    )}
                    {languages && (
                      <span style={chipStyle}>
                        <Translate style={{ fontSize: 13, color: "#555" }} />
                        {languages}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Divider + Next available ── */}
            {doctor.next_available_date && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 22px",
                  borderTop: "1px solid #f0f4f8",
                  borderBottom: "1px solid #f0f4f8",
                  background: "#fafcff",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <CalendarToday style={{ fontSize: 15, color: "#888" }} />
                  <Typography variant="body2" color="#666" fontWeight={500}>
                    Next available
                  </Typography>
                </div>
                <Typography variant="body2" fontWeight={600} color="#0d1b3d">
                  {formatDate(doctor.next_available_date)}
                </Typography>
              </div>
            )}

            {/* ── Shifts + Slots ── */}
            {slots && (
              <div style={{ padding: "4px 22px 16px" }}>
                <ShiftSection
                  title="Morning Shift"
                  shiftIcon={
                    <LightMode style={{ fontSize: 17, color: "#f59e0b" }} />
                  }
                  shiftData={slots.first_shift}
                  onSlotSelect={(slot, range) =>
                    handleSlotSelect(doctor, slot, range, "first")
                  }
                />
                <ShiftSection
                  title="Afternoon Shift"
                  shiftIcon={
                    <WbSunny style={{ fontSize: 17, color: "#f59e0b" }} />
                  }
                  shiftData={slots.second_shift}
                  onSlotSelect={(slot, range) =>
                    handleSlotSelect(doctor, slot, range, "second")
                  }
                />
                <ShiftSection
                  title="Evening Shift"
                  shiftIcon={
                    <Brightness3 style={{ fontSize: 17, color: "#6366f1" }} />
                  }
                  shiftData={slots.third_shift}
                  onSlotSelect={(slot, range) =>
                    handleSlotSelect(doctor, slot, range, "third")
                  }
                />
              </div>
            )}
          </div>
        );
      })}

      {selectedDoctor && selectedSlot && (
        <AppointmentBookingModal
          open={bookingModalOpen}
          onClose={handleBookingClose}
          doctor={selectedDoctor}
          slot={selectedSlot}
          patient={patient}
          job_id={job_id}
          orgCode={orgCode}
          appMode={appMode}
          authToken={authToken}
          onGoToMyAppointments={onGoToMyAppointments}
        />
      )}
    </div>
  );
};

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 10px",
  border: "1px solid #e0e8f0",
  borderRadius: 20,
  fontSize: 12,
  color: "#444",
  background: "#fff",
  whiteSpace: "nowrap",
};

export default DoctorSlotsDisplay;
